import {
  type DeleteFlowTestCaseResponse,
  DeleteFlowTestCaseResponseSchema,
  type FindAllFlowTestCases,
  type FlowTestCasePreflightView,
  FlowTestCasePreflightViewSchema,
  type FlowTestCaseView,
  FlowTestCaseViewSchema,
  type FlowTestRunDetail,
  FlowTestRunDetailSchema,
  type FlowTestRunView,
  FlowTestRunViewSchema,
  type RunFlowTestSuite,
  type RunFlowTestSuiteResponse,
  RunFlowTestSuiteResponseSchema,
  type StartFlowTestRunResponse,
  StartFlowTestRunResponseSchema,
} from 'flow-schema';
import { z } from 'zod';

import type { Common } from '../common.js';

export class API {
  common: Common;
  baseEndpoint = '/flow/flow-tests';

  constructor(common: Common) {
    this.common = common;
  }

  async findAllCases(query: FindAllFlowTestCases): Promise<FlowTestCaseView[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/cases`, query);
    return z.array(FlowTestCaseViewSchema).parse(await this.common.getJSON(url));
  }

  async findOneCase(testCaseId: string): Promise<FlowTestCaseView> {
    const url = this.common.constructPath(this.caseEndpoint(testCaseId));
    return FlowTestCaseViewSchema.parse(await this.common.getJSON(url));
  }

  async deleteCase(testCaseId: string): Promise<DeleteFlowTestCaseResponse> {
    const url = this.common.constructPath(this.caseEndpoint(testCaseId));
    return DeleteFlowTestCaseResponseSchema.parse(await this.common.deleteJSON(url));
  }

  async preflightCase(testCaseId: string): Promise<FlowTestCasePreflightView> {
    const url = this.common.constructPath(`${this.caseEndpoint(testCaseId)}/preflight`);
    return FlowTestCasePreflightViewSchema.parse(await this.common.getJSON(url));
  }

  async findAllRunsForCase(testCaseId: string): Promise<FlowTestRunView[]> {
    const url = this.common.constructPath(`${this.caseEndpoint(testCaseId)}/runs`);
    return z.array(FlowTestRunViewSchema).parse(await this.common.getJSON(url));
  }

  async runCase(testCaseId: string): Promise<StartFlowTestRunResponse> {
    const url = this.common.constructPath(`${this.caseEndpoint(testCaseId)}/runs`);
    return StartFlowTestRunResponseSchema.parse(await this.common.postJSON(url, {}));
  }

  async runSuite(body: RunFlowTestSuite): Promise<RunFlowTestSuiteResponse> {
    const url = this.common.constructPath(`${this.baseEndpoint}/runs`);
    return RunFlowTestSuiteResponseSchema.parse(await this.common.postJSON(url, body));
  }

  async findOneRun(flowRunId: string): Promise<FlowTestRunDetail> {
    const url = this.common.constructPath(`${this.baseEndpoint}/runs/${z.uuid().parse(flowRunId)}`);
    return FlowTestRunDetailSchema.parse(await this.common.getJSON(url));
  }

  private caseEndpoint(testCaseId: string): string {
    return `${this.baseEndpoint}/cases/${z.uuid().parse(testCaseId)}`;
  }
}
